import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import SummaryCard from "./components/SummaryCard";
import CompliancePanel from "./components/CompliancePanel";
import TroncTakingsChart from "./components/TroncTakingsChart";
import Icon from "../../components/AppIcon";
import { useTroncRecords } from "../../hooks/useTroncRecords";
import { useEmployees } from "../../hooks/useEmployees";
import { useDistributionRules } from "../../hooks/useDistributionRules";
import { useTenantContext } from "../../contexts/TenantContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { useAuth } from "../../contexts/AuthContext";
import { integrationsApi } from "../../services/api/integrations";
import { syncLogsApi } from "../../services/api/syncLogs";
import { apiClient } from "../../services/api/client";

const DATE_RANGES = [
  { id: "7days", label: "Last 7 Days", days: 7 },
  { id: "30days", label: "Last 30 Days", days: 30 },
  { id: "thisMonth", label: "This Month", days: null },
  { id: "lastMonth", label: "Last Month", days: null },
];

const DashboardOverview = () => {
  const [selectedDateRange, setSelectedDateRange] = useState("30days");
  const navigate = useNavigate();
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } = useTenantContext();
  const { currentLocation } = useLocationContext();
  const { isTenantAdmin } = useAuth();
  const shouldFetchData = !isGlobalAdmin || selectedTenant;
  const { records: troncRecords, loading: troncLoading, error: troncError } = useTroncRecords({ autoFetch: shouldFetchData });
  const { employees, loading: employeesLoading, error: employeesError } = useEmployees({ autoFetch: shouldFetchData });
  const { rules: distributionRules, loading: rulesLoading } = useDistributionRules({ autoFetch: shouldFetchData });
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [syncStats, setSyncStats] = useState(null);
  const [hasTroncMaster, setHasTroncMaster] = useState(null); // null = not checked, true/false = result

  useEffect(() => {
    if (!shouldFetchData) return;
    integrationsApi.getStatus().then((res) => {
      const data = res?.data ?? res?.Data ?? res;
      if (data) setIntegrationStatus(data);
    }).catch(() => {});
    syncLogsApi.getStats({ days: 30 }).then((res) => {
      const data = res?.data ?? res?.Data ?? res;
      if (data) setSyncStats(data);
    }).catch(() => {});
  }, [shouldFetchData, selectedTenant?.id]);

  // Check TroncMaster status for tenants with TroncMasterMode enabled
  useEffect(() => {
    const tenantInfo = selectedTenant;
    if (!shouldFetchData || !tenantInfo?.troncMasterMode) {
      setHasTroncMaster(null);
      return;
    }

    const checkTroncMaster = async () => {
      try {
        const usersRes = await apiClient.get("/users");
        const users = usersRes?.data ?? usersRes?.Data ?? [];
        const activeTM = users.some((u) => u.role === "TroncMaster" && u.isActive);
        setHasTroncMaster(activeTM);
      } catch {
        setHasTroncMaster(null);
      }
    };

    checkTroncMaster();
  }, [shouldFetchData, selectedTenant?.id, selectedTenant?.troncMasterMode]);

  const loading = troncLoading || employeesLoading || rulesLoading;
  const hasError = troncError || employeesError;

  const dateFilter = useMemo(() => {
    const now = new Date();
    let start, end;
    switch (selectedDateRange) {
      case "7days": start = new Date(); start.setDate(now.getDate() - 7); end = now; break;
      case "30days": start = new Date(); start.setDate(now.getDate() - 30); end = now; break;
      case "thisMonth": start = new Date(now.getFullYear(), now.getMonth(), 1); end = now; break;
      case "lastMonth": start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
    default: start = new Date(); start.setDate(now.getDate() - 30); end = now;
    }
    return { start, end };
  }, [selectedDateRange]);

  const displayRecords = useMemo(() => {
    let records = troncRecords || [];
    if (currentLocation) records = records.filter((r) => r.locationId === currentLocation.id);
    return records.filter((r) => { const d = new Date(r.periodStart); return d >= dateFilter.start && d <= dateFilter.end; });
  }, [troncRecords, currentLocation, dateFilter]);

  const eligibleEmployees = useMemo(() => {
    let emps = (employees || []).filter((e) => e.isEligibleForTronc && e.isActive);
    if (currentLocation) emps = emps.filter((e) => e.locationId === currentLocation.id);
    return emps;
  }, [employees, currentLocation]);

  const totalTronc = displayRecords.reduce((sum, r) => sum + (r.amount || 0), 0);
  const pendingRecords = displayRecords.filter((r) => r.status === "Pending" || r.status === "Approved" || r.status === 1 || r.status === 2);
  const pendingAmount = pendingRecords.reduce((sum, r) => sum + (r.amount || 0), 0);
  const distributedRecordsList = displayRecords.filter((r) => r.isDistributed || r.status === "Distributed" || r.status === 3);
  const distributedAmount = distributedRecordsList.reduce((sum, r) => sum + (r.amount || 0), 0);
  const activeRule = useMemo(() => (distributionRules || []).find((r) => r.isActive && !r.isDraft), [distributionRules]);
  const eligibilitySummary = useMemo(() => {
    const allActive = (employees || []).filter((e) => e.isActive);
    const eligible = allActive.filter((e) => e.isEligibleForTronc);
    return { total: allActive.length, eligible: eligible.length, ineligible: allActive.length - eligible.length };
  }, [employees]);

  const complianceData = [
    { id: 1, title: "UK Employment Law Adherence", description: "Distribution rules configured for compliance", status: activeRule ? "Compliant" : "Review Required", lastChecked: new Date().toLocaleDateString("en-GB") },
    { id: 2, title: "HMRC Guidance Compliance", description: "TRONC scheme setup follows HMRC guidelines", status: "Compliant", lastChecked: new Date().toLocaleDateString("en-GB") },
    { id: 3, title: "Record Keeping Standards", description: "Audit trail active for all transactions", status: "Compliant", lastChecked: new Date().toLocaleDateString("en-GB") },
    { id: 4, title: "Employee Rights Protection", description: "Fair distribution methods in place", status: eligibilitySummary.eligible > 0 ? "Compliant" : "Review Required", lastChecked: new Date().toLocaleDateString("en-GB") },
    // TroncMaster Independence — only show when tenant has TroncMasterMode enabled
    ...(selectedTenant?.troncMasterMode
      ? [{
    id: 5,
    title: "TRONC Master Independence",
          description: hasTroncMaster
 ? "An independent TRONC Master is assigned and active"
   : "TRONC Master Mode is enabled but no TRONC Master user has been assigned. HMRC requires an independent TRONC Master to manage distributions.",
  status: hasTroncMaster === true ? "Compliant" : "Non-Compliant",
    lastChecked: new Date().toLocaleDateString("en-GB"),
        }]
      : []),
  ];

  const breadcrumbs = [{ label: "Dashboard", path: "/dashboard-overview" }];
  const formatCurrency = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v);
  const formatSyncDate = (d) => {
    if (!d) return "Never";
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
 <Helmet><title>Dashboard - Rocket Tronc</title></Helmet>
  <PageLayout title="Dashboard Overview" description="Monitor your TRONC distribution performance and access key management functions." breadcrumbs={breadcrumbs} showBreadcrumbs={false}>
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6"><Icon name="Building2" size={40} className="text-primary" /></div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">As a Global Administrator, please select a tenant from the sidebar to view their dashboard and manage their TRONC data.</p>
     <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground"><Icon name="ArrowLeft" size={16} /><span>Use the tenant selector in the sidebar</span></div>
     </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Dashboard - {selectedTenant?.name || "Rocket Tronc"}</title></Helmet>
      <PageLayout
      title="Dashboard Overview"
        description={selectedTenant && currentLocation ? `Viewing ${selectedTenant.name} - ${currentLocation.name}` : selectedTenant ? `Viewing ${selectedTenant.name} - All Locations` : "Monitor your TRONC distribution performance."}
        breadcrumbs={breadcrumbs}
     showBreadcrumbs={false}
      >
      {/* Page-Level Date Filter */}
        <div className="bg-card rounded-xl border border-border p-4 mb-6">
          <div className="flex flex-wrap items-center gap-2">
   <span className="text-sm font-medium text-muted-foreground mr-2">Filter Dashboard for Period:</span>
  {DATE_RANGES.map((range) => (
              <button key={range.id} onClick={() => setSelectedDateRange(range.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedDateRange === range.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
      <Icon name="Calendar" size={14} />{range.label}
</button>
            ))}
          </div>
        </div>

        {loading && (<div className="card p-8 text-center mb-6"><div className="spinner mx-auto mb-4" /><p className="text-muted-foreground">Loading dashboard data...</p></div>)}
        {hasError && !loading && (<div className="alert alert-warning mb-6"><Icon name="AlertTriangle" size={20} /><div><p className="alert-title">Unable to load some data</p><p className="alert-description">Please ensure the API server is running.</p></div></div>)}

        {/* Summary Cards */}
        <PageSection>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
 <SummaryCard title="Total TRONC" amount={formatCurrency(totalTronc)} subtitle={`${displayRecords.length} records in period`} icon="PoundSterling" iconColor="var(--color-primary)" />
    <SummaryCard title="Pending Distribution" amount={formatCurrency(pendingAmount)} subtitle={`${pendingRecords.length} ${pendingRecords.length === 1 ? "record" : "records"} awaiting`} icon="Clock" iconColor="var(--color-warning)" />
   <SummaryCard title="Distributed" amount={formatCurrency(distributedAmount)} subtitle={`${distributedRecordsList.length} completed`} icon="CheckCircle2" iconColor="var(--color-success)" />
      <SummaryCard title="Eligible Employees" amount={eligibleEmployees.length.toString()} subtitle={currentLocation ? currentLocation.name : "All locations"} icon="Users" iconColor="var(--color-secondary)" />
          </div>
        </PageSection>

     {/* TRONC Takings Chart */}
        <PageSection title="TRONC Takings Trend">
    <TroncTakingsChart troncRecords={troncRecords || []} locationFilter={currentLocation?.id} />
        </PageSection>

        {/* Active Rule + Eligibility + Sync Status */}
        <PageSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Active Distribution Rule */}
   <div className="bg-card rounded-xl border border-border p-5">
  <div className="flex items-center gap-3 mb-4">
       <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon name="Settings2" size={20} className="text-primary" /></div>
     <div className="flex-1"><h3 className="font-semibold text-sm">Active Distribution Rule</h3></div>
              {isTenantAdmin && activeRule && (<button onClick={() => navigate("/settings/distribution-rules")} className="text-xs text-primary hover:underline flex items-center gap-1"><Icon name="ExternalLink" size={12} /> Edit</button>)}
        </div>
        {activeRule ? (
        <div>
  <p className="text-lg font-bold mb-1">{activeRule.name}</p>
         <p className="text-sm text-muted-foreground mb-2">{activeRule.type || activeRule.distributionType}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
      {activeRule.enableHourFactor && <span className="badge badge-secondary">Hour Factor</span>}
       {activeRule.enableSeniorityFactor && <span className="badge badge-secondary">Seniority Factor</span>}
                 {activeRule.enableMinimumFloor && <span className="badge badge-secondary">Min. Floor</span>}
       </div>
          {activeRule.effectiveFrom && (<p className="text-xs text-muted-foreground mt-2">Effective from {new Date(activeRule.effectiveFrom).toLocaleDateString("en-GB")}</p>)}
          </div>
        ) : (
           <div className="text-center py-4">
   <Icon name="AlertTriangle" size={24} className="mx-auto mb-2 text-warning" />
        <p className="text-sm text-muted-foreground mb-2">No active rule configured</p>
         {isTenantAdmin && (<button onClick={() => navigate("/settings/distribution-rules")} className="btn btn-primary btn-sm">Configure Rule</button>)}
              </div>
      )}
            </div>

{/* Eligibility Summary */}
            <div className="bg-card rounded-xl border border-border p-5">
           <div className="flex items-center gap-3 mb-4">
 <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center"><Icon name="UserCheck" size={20} className="text-success" /></div>
      <div className="flex-1"><h3 className="font-semibold text-sm">Eligibility Summary</h3></div>
          {isTenantAdmin && (<button onClick={() => navigate("/system/eligibility")} className="text-xs text-primary hover:underline flex items-center gap-1"><Icon name="ExternalLink" size={12} /> Edit</button>)}
     </div>
    <div className="space-y-3">
  <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Total Active</span><span className="font-bold text-lg">{eligibilitySummary.total}</span></div>
          <div className="flex items-center justify-between"><span className="text-sm text-success">Eligible</span><span className="font-bold text-lg text-success">{eligibilitySummary.eligible}</span></div>
     <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Ineligible</span><span className="font-bold text-lg">{eligibilitySummary.ineligible}</span></div>
         {eligibilitySummary.total > 0 && (<div className="h-2 bg-muted rounded-full overflow-hidden mt-2"><div className="h-full bg-success rounded-full" style={{ width: `${(eligibilitySummary.eligible / eligibilitySummary.total) * 100}%` }} /></div>)}
       </div>
            </div>

     {/* Sync Status */}
  <div className="bg-card rounded-xl border border-border p-5">
 <div className="flex items-center gap-3 mb-4">
  <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center"><Icon name="RefreshCw" size={20} className="text-accent" /></div>
     <div className="flex-1"><h3 className="font-semibold text-sm">Sync Status</h3></div>
      <button onClick={() => navigate("/settings/sync-logs")} className="text-xs text-primary hover:underline flex items-center gap-1"><Icon name="ExternalLink" size={12} /> View Logs</button>
    </div>
        <div className="space-y-4">
              <div>
            <div className="flex items-center gap-2 mb-1"><Icon name="Users" size={14} className="text-muted-foreground" /><span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last HR Sync</span></div>
          <p className="text-sm font-semibold">{formatSyncDate(integrationStatus?.lastHrSync)}</p>
    </div>
    <div>
   <div className="flex items-center gap-2 mb-1"><Icon name="PoundSterling" size={14} className="text-muted-foreground" /><span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Last Till Sync</span></div>
            <p className="text-sm font-semibold">{formatSyncDate(integrationStatus?.lastTillSync)}</p>
                </div>
      {syncStats && (
     <div className="pt-2 border-t border-border">
 <div className="flex items-center justify-between text-xs text-muted-foreground">
           <span>{syncStats.totalSyncs} syncs (30d)</span>
     <span className={syncStats.failedSyncs > 0 ? "text-error font-medium" : "text-success"}>{syncStats.failedSyncs > 0 ? `${syncStats.failedSyncs} failed` : "All OK"}</span>
     </div>
       </div>
     )}
 </div>
            </div>
    </div>
        </PageSection>

        {/* Compliance Panel */}
        <PageSection title="Compliance Status">
 <CompliancePanel complianceData={complianceData} />
   </PageSection>
      </PageLayout>
    </>
  );
};

export default DashboardOverview;
